import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { apiGetVibeProcess } from "../../../services/WorkflowService";
import { apiGetCustomPropertiesForWorkspace } from "../../../services/EmployeeRecordService";
import { VibeProcess, CustomProperty, CustomPropertyValue, Workflow, Task, CustomPropertyType, VibeProcessLayout } from "winslow-core";
import { Button, Icon } from "../../../ds2/components";
import { MyTooltip } from "../../common/MyTooltip";
import { 
  Info,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Shield,
  X
} from "lucide-react";
import "./AppVibeProcess.css";
import "../app-customize/AppCustomProperty.css";
import { AppCustomProperty } from "../app-customize/AppCustomProperty";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { AppChanges } from "../app-changes/AppChanges";
import { EmployeeRecordSelect } from "../../shared/EmployeeRecordSelect";

type AppVibeProcessProps = {
    dynamicContext?: {
        vibeProcessId: string; // Optional context for employee records
        employeeRecordIds?: string[];
        workflow: Workflow | null;
    };
    disabled?: boolean;
    onSubmit?: (propertyValues: Map<string, any>) => void;
    requiredProperties?: string[]; // Array of required property labels for this vibe process
    creatorEmployeeRecordId?: string;
    onCreatorEmployeeRecordIdChange?: (value: string) => void;
}

export const AppVibeProcess = ({ dynamicContext, disabled = false, onSubmit, requiredProperties = [], creatorEmployeeRecordId, onCreatorEmployeeRecordIdChange }: AppVibeProcessProps) => {
  const dispatch = useDispatch();

  const storeUser = useSelector(selectUser);
  const uiStates = useSelector(selectUIStates);
  
  const [vibeProcess, setVibeProcess] = useState<VibeProcess | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Custom properties state
  const [workspaceCustomProperties, setWorkspaceCustomProperties] = useState<CustomProperty[]>([]);
  const [vibeProcessProperties, setVibeProcessProperties] = useState<CustomProperty[]>([]);
  const [customPropertyValues, setCustomPropertyValues] = useState<Map<string, any>>(new Map());
  
  // Helper function to check if a property is required for this vibe process
  const isPropertyRequired = (propertyLabel: string): boolean => {
    return requiredProperties.includes(propertyLabel);
  };
  
  // Two-column layout state
  const [leftColumnProperties, setLeftColumnProperties] = useState<CustomProperty[]>([]);
  const [rightColumnProperties, setRightColumnProperties] = useState<CustomProperty[]>([]);
  
  // Menu state
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // default for the custom properties
  const useEmployeeRecordId = dynamicContext?.employeeRecordIds?.[0] ?
    dynamicContext.employeeRecordIds[0] : 
    storeUser?.employeeRecordId;

//   useEffect(() => {
//     dispatch(setUIStates({...uiStates, hrisLocation: "Vibe Process"}));
//   }, []);

  
  useEffect(() => {
    if (dynamicContext?.workflow) {
      const workflowCPV = JSON.parse(dynamicContext.workflow.customProperties || "[]") as CustomPropertyValue[];
      const workflowCPVMap = new Map<string, any>();
      workflowCPV.forEach((cpv: CustomPropertyValue) => {
        // alert(`Setting custom property value: ${cpv.label} = ${cpv.data}`);
        workflowCPVMap.set(cpv.label, cpv.data);
      });
      console.error("[AppVibeProcess] workflowCPVMap: ", workflowCPVMap);
      setCustomPropertyValues(workflowCPVMap);
    }
  }, [dynamicContext?.workflow]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenu && !(event.target as Element).closest('.app-vibe-process-property-menu')) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenu]);

  useEffect(() => {
    const loadData = async () => {
      if (dynamicContext?.vibeProcessId && dynamicContext.vibeProcessId !== "new") {
        await loadVibeProcess(dynamicContext.vibeProcessId);
      } else {
        setLoading(false);
      }
    };

    loadData();
  }, [dynamicContext?.vibeProcessId]);  


  const loadVibeProcess = async (processId: string) => {
    if (!storeUser?.currentWorkspaceId) return;

    try {
      setLoading(true);
      
      // Load workspace custom properties
      const workspaceCustomProperties = await apiGetCustomPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");

      if (!workspaceCustomProperties) {
        toast("Failed to load workspace custom properties.", toastFail);
        return;
      }
      
      setWorkspaceCustomProperties(workspaceCustomProperties);

      // Load vibe process
      const process = await apiGetVibeProcess(processId);

      if (!process) {
        toast(`Failed to load vibe process: ${processId}`, toastFail);
        return;
      }
      
      if (process) {
        setVibeProcess(process);
        
        // Note: requiredProperties should be passed via props (e.g., from DCVibeProcess)
        // If not provided, we parse it from the process, but props take precedence
        
        // Parse custom properties from customProperties field
        parseVibeProcessProperties(process.customProperties, workspaceCustomProperties, process.layout);
        
        // Parse custom property values from workflow if available, otherwise use process defaults
        // The workflow customProperties contain the actual values for this workflow instance
        if (dynamicContext?.workflow?.customProperties) {
          parseCustomPropertyValues(dynamicContext.workflow.customProperties);
        } else {
          // If no workflow context, initialize with empty values (for new workflows)
          parseCustomPropertyValues("[]");
        }
        
      }
    } catch (error) {
      console.error("Error loading vibe process:", error);
    } finally {
      setLoading(false);
    }
  };

  const parseVibeProcessProperties = (customPropertiesJson: string, workspaceCustomProperties: CustomProperty[], layoutJson?: string) => {
    try {
      console.log("parseVibeProcessProperties called with:", customPropertiesJson);
      console.log("Layout JSON:", layoutJson);
      
      // Parse the custom properties JSON
      const properties = JSON.parse(customPropertiesJson || "[]");
      console.log("Parsed properties:", properties);
      
      console.log("Available workspace properties:", workspaceCustomProperties);
      
      const vibeProcessProps: CustomProperty[] = [];
      
      properties.forEach((prop: any) => {
        console.log("Processing property:", prop.label, "type:", prop.type);
        // Find matching custom property in workspace (treat all properties the same)
        const matchingProp = workspaceCustomProperties.find(cp => cp.label === prop.label);
        if (matchingProp) {
          console.log("Found matching workspace property:", matchingProp.label);
          vibeProcessProps.push(matchingProp);
        } else {
          console.log("No matching workspace property found for:", prop.label);
        }
      });
      
      console.log("Final vibe process properties:", vibeProcessProps.length, "properties");
      setVibeProcessProperties(vibeProcessProps);
      
      // Apply layout if it exists, otherwise use default distribution
      if (layoutJson) {
        try {
          const layout = JSON.parse(layoutJson) as VibeProcessLayout;
          applyLayoutToProperties(vibeProcessProps, layout);
        } catch (layoutError) {
          console.error("Error parsing layout, using default distribution:", layoutError);
          distributePropertiesToColumns(vibeProcessProps);
        }
      } else {
        distributePropertiesToColumns(vibeProcessProps);
      }
      
      console.log("Left column properties:", leftColumnProperties.length);
      console.log("Right column properties:", rightColumnProperties.length);
    } catch (error) {
      console.error("Error parsing vibe process properties:", error);
      setVibeProcessProperties([]);
      setLeftColumnProperties([]);
      setRightColumnProperties([]);
    }
  };

  const distributePropertiesToColumns = (properties: CustomProperty[]) => {
    console.log("distributePropertiesToColumns called with:", properties.length, "properties");
    const leftColumn: CustomProperty[] = [];
    const rightColumn: CustomProperty[] = [];
    
    properties.forEach((property, index) => {
      if (index % 2 === 0) {
        leftColumn.push(property);
      } else {
        rightColumn.push(property);
      }
    });
    
    console.log("Distributed to columns - Left:", leftColumn.length, "Right:", rightColumn.length);
    setLeftColumnProperties(leftColumn);
    setRightColumnProperties(rightColumn);
  };

  const applyLayoutToProperties = (properties: CustomProperty[], layout: VibeProcessLayout) => {
    console.log("applyLayoutToProperties called with layout:", layout);
    
    // Create a map of property labels to properties for quick lookup
    const propertyMap = new Map<string, CustomProperty>();
    properties.forEach(prop => {
      propertyMap.set(prop.label, prop);
    });
    
    // Build left column from layout
    const leftColumn: CustomProperty[] = [];
    layout.leftColumn.forEach(label => {
      const prop = propertyMap.get(label);
      if (prop) {
        leftColumn.push(prop);
      }
    });
    
    // Build right column from layout
    const rightColumn: CustomProperty[] = [];
    layout.rightColumn.forEach(label => {
      const prop = propertyMap.get(label);
      if (prop) {
        rightColumn.push(prop);
      }
    });
    
    // Add any properties not in the layout to the left column (in case layout is incomplete)
    properties.forEach(prop => {
      if (!layout.leftColumn.includes(prop.label) && !layout.rightColumn.includes(prop.label)) {
        leftColumn.push(prop);
      }
    });
    
    console.log("Applied layout - Left:", leftColumn.length, "Right:", rightColumn.length);
    setLeftColumnProperties(leftColumn);
    setRightColumnProperties(rightColumn);
  };

  const parseCustomPropertyValues = (customPropertiesJson: string) => {
    try {
      const propertyValues = JSON.parse(customPropertiesJson || "[]") as CustomPropertyValue[];
      const valuesMap = new Map<string, any>();
      
      propertyValues.forEach((prop) => {
        valuesMap.set(prop.label, prop.data);
      });
      
      setCustomPropertyValues(valuesMap);
    } catch (error) {
      console.error("Error parsing custom property values:", error);
      setCustomPropertyValues(new Map());
    }
  };

  const handleCustomPropertyChange = (label: string, data: any) => {
    const updatedValues = new Map(customPropertyValues);
    updatedValues.set(label, data);
    setCustomPropertyValues(updatedValues);
    setHasChanges(true);
  };

  const handleMenuToggle = (propertyLabel: string) => {
    setOpenMenu(openMenu === propertyLabel ? null : propertyLabel);
  };

  const handleMoveToColumn = (propertyLabel: string, fromColumn: string) => {
    const targetColumn = fromColumn === 'left-column' ? 'right-column' : 'left-column';
    
    if (fromColumn === 'left-column') {
      // Move from left to right
      const property = leftColumnProperties.find(p => p.label === propertyLabel);
      if (property) {
        const newLeftColumn = leftColumnProperties.filter(p => p.label !== propertyLabel);
        const newRightColumn = [...rightColumnProperties, property];
        setLeftColumnProperties(newLeftColumn);
        setRightColumnProperties(newRightColumn);
      }
    } else {
      // Move from right to left
      const property = rightColumnProperties.find(p => p.label === propertyLabel);
      if (property) {
        const newRightColumn = rightColumnProperties.filter(p => p.label !== propertyLabel);
        const newLeftColumn = [...leftColumnProperties, property];
        setRightColumnProperties(newRightColumn);
        setLeftColumnProperties(newLeftColumn);
      }
    }
    
    setOpenMenu(null);
  };

  const handleMoveUp = (propertyLabel: string, columnId: string) => {
    if (columnId === 'left-column') {
      const index = leftColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index > 0) {
        const newColumn = [...leftColumnProperties];
        [newColumn[index - 1], newColumn[index]] = [newColumn[index], newColumn[index - 1]];
        setLeftColumnProperties(newColumn);
      }
    } else {
      const index = rightColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index > 0) {
        const newColumn = [...rightColumnProperties];
        [newColumn[index - 1], newColumn[index]] = [newColumn[index], newColumn[index - 1]];
        setRightColumnProperties(newColumn);
      }
    }
    
    setOpenMenu(null);
  };

  const handleMoveDown = (propertyLabel: string, columnId: string) => {
    if (columnId === 'left-column') {
      const index = leftColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index < leftColumnProperties.length - 1) {
        const newColumn = [...leftColumnProperties];
        [newColumn[index], newColumn[index + 1]] = [newColumn[index + 1], newColumn[index]];
        setLeftColumnProperties(newColumn);
      }
    } else {
      const index = rightColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index < rightColumnProperties.length - 1) {
        const newColumn = [...rightColumnProperties];
        [newColumn[index], newColumn[index + 1]] = [newColumn[index + 1], newColumn[index]];
        setRightColumnProperties(newColumn);
      }
    }
    
    setOpenMenu(null);
  };

  const handleMakeRequired = (propertyLabel: string) => {
    alert(`Make Required: ${propertyLabel}\n\nThis will make the property required for the workflow.`);
    setOpenMenu(null);
  };

  const handleDeleteProperty = (propertyLabel: string) => {
    const confirmed = window.confirm(`Delete Property: ${propertyLabel}\n\nAre you sure you want to delete this property? This action cannot be undone.`);
    if (confirmed) {
      alert(`Property "${propertyLabel}" would be deleted.`);
      setOpenMenu(null);
    }
  };

  console.log("[AppVibeProcess] dynamicContext:", dynamicContext);
  console.log("[AppVibeProcess] leftColumnProperties:", leftColumnProperties);
  console.log("[AppVibeProcess] rightColumnProperties:", rightColumnProperties);
  console.log("[AppVibeProcess] customPropertyValues:", customPropertyValues);
  // console.log("[AppVibeProcess] vibeProcess:", vibeProcess);
  // console.log("[AppVibeProcess] vibeProcessProperties:", vibeProcessProperties);
  // console.log("[AppVibeProcess] rightColumnProperties:", rightColumnProperties);
  // console.log("[AppVibeProcess] leftColumnProperties:", leftColumnProperties);

  if (loading) {
    return (
      <div className="app-vibe-process">
        <div className="app-vibe-process-loading">
          <div className="app-vibe-process-loading-spinner"></div>
          <p>Loading vibe process...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-vibe-process">
      <div className="app-vibe-process-header dc-component-header">
        <div className="app-vibe-process-header-left">
          {vibeProcess?.name || "Vibe Process"}
        </div>
        {onCreatorEmployeeRecordIdChange && (
          <div className="app-vibe-process-header-right">
            <label className="app-vibe-process-for-label">For:</label>
            <EmployeeRecordSelect
              selected={creatorEmployeeRecordId}
              disabled={disabled}
              onChange={(value: string) => onCreatorEmployeeRecordIdChange(value)}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="app-vibe-process-content">
        {(leftColumnProperties.length > 0 || rightColumnProperties.length > 0) && (
            <div className="app-vibe-process-two-column-layout">
              {/* Left Column */}
              <div className="app-vibe-process-column">
                  {leftColumnProperties.map((customProperty: CustomProperty, index: number) => (
                    <div
                    key={customProperty.label}
                    className="app-vibe-process-property"
                  >
                    <div className="app-vibe-process-property-label">
                      {customProperty.display}
                      {customProperty.description && (
                      <MyTooltip title={customProperty.description}>
                        <Info size={16} className="app-vibe-process-info-icon" />
                      </MyTooltip>
                      )}
                      {!isPropertyRequired(customProperty.label) && (
                        <div className="app-vibe-process-property-optional">
                          (OPTIONAL)
                        </div>
                      )}
                    </div>
                    
                    <div className="app-vibe-process-property">
                      <AppCustomProperty
                        // disabled={disabled}
                        customProperty={customProperty}
                        data={customPropertyValues.get(customProperty.label) || undefined}
                        validate={false}
                        onChange={handleCustomPropertyChange}
                      />
                    </div> 
                  </div>
                  ))}
              </div>

              {/* Right Column */}
              <div className="app-vibe-process-column">
                <div className="app-vibe-process-column-content">
                  {rightColumnProperties.map((customProperty: CustomProperty, index: number) => (
                    <div
                      key={customProperty.label}
                      className="app-vibe-process-property"
                    >
                      <div className="app-vibe-process-property-label">
                        {customProperty.display}
                        {customProperty.description && (
                        <MyTooltip title={customProperty.description}>
                          <Info size={16} className="app-vibe-process-info-icon" />
                        </MyTooltip>
                        )}
                        {!customProperty.required && (
                          <div className="app-vibe-process-property-optional">
                            (OPTIONAL)
                          </div>
                        )}
                      </div>
                      
                      <div className="app-vibe-process-property">
                        <AppCustomProperty
                          // disabled={disabled}
                          customProperty={customProperty}
                          data={customPropertyValues.get(customProperty.label) || undefined}
                          validate={false}
                          onChange={handleCustomPropertyChange}
                        />
                      </div> 
                    </div>
                  ))}
                </div>
              </div>
            </div>
        )}
      </div>

      {hasChanges && onSubmit && !disabled &&
        <AppChanges
          submitText="Submit"
          onSubmit={() => {
            setHasChanges(false);
            onSubmit(customPropertyValues)
          }}
          onCancel={() => setHasChanges(false)}
        />
      }
    </div>
  );
};
